import { useMemo, useState, type FormEvent } from 'react';
import { api } from '../api';
import { formatEuros, localDate, shortDate } from '../format';
import type { Obligation, ProrataResult } from '../types';

const METHODS: Array<{ value: string; label: string }> = [
  { value: 'VIREMENT', label: 'Virement' },
  { value: 'ESPECES', label: 'Espèces' },
  { value: 'CHEQUE', label: 'Chèque' },
  { value: 'AUTRE', label: 'Autre' },
  { value: 'STUDAPART', label: 'Studapart (loyer reversé, commission déduite à part)' },
];

function remaining(obligation: Obligation): number {
  return Math.max(0, obligation.amountCents - obligation.paidCents);
}

/**
 * Calculette de prorata intégrée à la saisie du versement, pour vérifier ou
 * retrouver un montant sur une période choisie sans quitter le formulaire.
 * Ne modifie aucune échéance : elle propose seulement un montant à reprendre.
 */
function ProrataTool({ leaseId, onApply }: { leaseId: string; onApply: (cents: number) => void }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(localDate());
  const [to, setTo] = useState(localDate());
  const [method, setMethod] = useState<'JOURS_REELS' | 'TRENTIEMES'>('JOURS_REELS');
  const [result, setResult] = useState<ProrataResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function compute(): Promise<void> {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const params = new URLSearchParams({ from, to, method });
      setResult(await api<ProrataResult>(`/api/admin/leases/${leaseId}/prorata?${params.toString()}`));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Calcul impossible.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return <button type="button" className="text-button" onClick={() => setOpen(true)}>Proratiser…</button>;
  }

  return (
    <div className="prorata-tool">
      <div className="form-grid">
        <label>Du
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>Au
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
        <label>Méthode
          <select value={method} onChange={(event) => setMethod(event.target.value as 'JOURS_REELS' | 'TRENTIEMES')}>
            <option value="JOURS_REELS">Jours réels</option>
            <option value="TRENTIEMES">Trentièmes</option>
          </select>
        </label>
      </div>
      <div className="record-actions">
        <button type="button" className="text-button" onClick={() => { setOpen(false); setResult(null); setError(''); }}>Fermer</button>
        <button type="button" className="button secondary compact" disabled={busy} onClick={() => void compute()}>
          {busy ? 'Calcul…' : 'Calculer'}
        </button>
      </div>
      {error && <div className="inline-message error" role="alert">{error}</div>}
      {result && (
        <div className="prorata-result">
          <p>{result.formula} : loyer {formatEuros(result.rentCents)} + charges {formatEuros(result.chargesCents)} = <strong>{formatEuros(result.totalCents)}</strong></p>
          <button type="button" className="button primary compact" onClick={() => { onApply(result.totalCents); setOpen(false); setResult(null); }}>
            Utiliser ce montant
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Saisie d'un versement et de son affectation aux échéances.
 *
 * Le montant reçu et son affectation sont volontairement distincts : un
 * locataire peut verser une somme ronde couvrant deux mois, ou un acompte qui
 * n'en solde aucun. Forcer l'égalité obligerait à inventer des écritures.
 */
export function PaymentForm({ lease, obligations, onDone, onCancel }: {
  lease: { id: string; roomName: string; tenantName: string };
  obligations: Obligation[];
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const unpaid = useMemo(
    () => obligations.filter((obligation) => remaining(obligation) > 0)
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [obligations],
  );
  // Par défaut, la date de l'échéance déjà présélectionnée (la plus ancienne
  // impayée), pas la date du jour : un loyer de janvier réglé à temps par le
  // locataire mais saisi en retard, en avril, ne doit pas se retrouver
  // compté comme une recette d'avril. Reste librement modifiable pour un
  // versement réellement tardif, où la date du jour n'est plus la bonne non
  // plus : c'est la date réelle de réception qui compte, jamais l'échéance
  // ni la date de saisie.
  const [receivedOn, setReceivedOn] = useState(() => unpaid[0]?.dueDate ?? localDate());
  const [method, setMethod] = useState('VIREMENT');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState<Record<string, number>>(
    () => (unpaid[0] ? { [unpaid[0].id]: remaining(unpaid[0]) } : {}),
  );
  const [amountEuros, setAmountEuros] = useState(
    () => (unpaid[0] ? (remaining(unpaid[0]) / 100).toFixed(2) : ''),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const allocatedCents = Object.values(selected).reduce((total, cents) => total + cents, 0);
  const amountCents = Math.round(Number(amountEuros.replace(',', '.')) * 100) || 0;
  const overAllocated = allocatedCents > amountCents;
  const unallocated = amountCents - allocatedCents;

  function toggle(obligation: Obligation): void {
    setSelected((current) => {
      const next = { ...current };
      if (obligation.id in next) delete next[obligation.id];
      else next[obligation.id] = remaining(obligation);
      const total = Object.values(next).reduce((sum, cents) => sum + cents, 0);
      setAmountEuros((total / 100).toFixed(2));
      return next;
    });
  }

  function setAllocation(obligationId: string, euros: string): void {
    const cents = Math.round(Number(euros.replace(',', '.')) * 100) || 0;
    setSelected((current) => ({ ...current, [obligationId]: cents }));
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/api/admin/payments', {
        method: 'POST',
        body: JSON.stringify({
          leaseId: lease.id,
          receivedOn,
          amountCents,
          method,
          reference: reference.trim(),
          note: note.trim(),
          allocations: Object.entries(selected)
            .filter(([, cents]) => cents > 0)
            .map(([obligationId, cents]) => ({ obligationId, amountCents: cents })),
        }),
      });
      await onDone();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="payment-form" onSubmit={(event) => void submit(event)}>
      <div className="section-heading">
        <div><p className="eyebrow">Versement reçu</p><h3>{lease.roomName} · {lease.tenantName}</h3></div>
        <button type="button" className="text-button" onClick={onCancel}>Fermer</button>
      </div>

      <div className="form-grid">
        <label>Montant reçu (€)
          <input inputMode="decimal" value={amountEuros} onChange={(event) => setAmountEuros(event.target.value)} required />
        </label>
        <div className="prorata-slot">
          <ProrataTool leaseId={lease.id} onApply={(cents) => setAmountEuros((cents / 100).toFixed(2))} />
        </div>
        <label>Reçu le
          <input type="date" value={receivedOn} onChange={(event) => setReceivedOn(event.target.value)} required />
        </label>
        <label>Moyen
          <select value={method} onChange={(event) => setMethod(event.target.value)}>
            {METHODS.map((entry) => <option key={entry.value} value={entry.value}>{entry.label}</option>)}
          </select>
        </label>
        <label>Référence
          <input value={reference} onChange={(event) => setReference(event.target.value)} placeholder="Libellé du virement" />
        </label>
        <label className="span-2">Note
          <input value={note} onChange={(event) => setNote(event.target.value)} />
        </label>
      </div>

      <fieldset className="allocation-field">
        <legend>Affecter à</legend>
        {unpaid.length === 0 && <p className="muted">Toutes les échéances de ce bail sont soldées.</p>}
        {unpaid.map((obligation) => {
          const checked = obligation.id in selected;
          return (
            <div className={`allocation-row ${checked ? 'active' : ''}`} key={obligation.id}>
              <label className="allocation-pick">
                <input type="checkbox" checked={checked} onChange={() => toggle(obligation)} />
                <span>
                  <strong>{obligation.kind === 'SECURITY_DEPOSIT' ? 'Dépôt de garantie' : obligation.period}</strong>
                  <small>échéance {shortDate(obligation.dueDate)} · reste {formatEuros(remaining(obligation))}</small>
                </span>
              </label>
              {checked && (
                <label className="allocation-amount">
                  <input
                    inputMode="decimal"
                    value={((selected[obligation.id] ?? 0) / 100).toFixed(2)}
                    onChange={(event) => setAllocation(obligation.id, event.target.value)}
                  /> €
                </label>
              )}
            </div>
          );
        })}
      </fieldset>

      <div className={`allocation-summary ${overAllocated ? 'over' : ''}`}>
        <span>Affecté : <strong>{formatEuros(allocatedCents)}</strong> sur {formatEuros(amountCents)}</span>
        {overAllocated && <span className="warn">L'affectation dépasse le montant reçu.</span>}
        {!overAllocated && unallocated > 0 && (
          <span className="warn">{formatEuros(unallocated)} non affecté : le versement sera enregistré, mais cette part ne soldera aucune échéance.</span>
        )}
      </div>

      {error && <div className="inline-message error" role="alert">{error}</div>}
      <div className="record-actions">
        <button type="button" className="text-button" onClick={onCancel}>Annuler</button>
        <button className="button primary compact" disabled={busy || amountCents <= 0 || overAllocated}>
          {busy ? 'Enregistrement…' : 'Enregistrer le versement'}
        </button>
      </div>
    </form>
  );
}
